import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { MapPin, Calendar, Users, ListChecks, Building2, FileText } from 'lucide-react'
import { cn } from 'lib/utils'
import { useOutletContext } from 'react-router-dom'
import type { EvaluationOutletContext } from '../Evaluations_ShowCN'

export const STATUS_COLORS: Record<string, string> = {
	DRAFT: 'bg-gray-100 text-gray-700',
	IN_ANALYSIS: 'bg-blue-100 text-blue-700',
	UNDER_REVIEW: 'bg-amber-100 text-amber-700',
	APPROVED: 'bg-green-100 text-green-700',
	REJECTED: 'bg-red-100 text-red-700',
	ARCHIVED: 'bg-gray-200 text-gray-500',
}

// Counts come from the evaluation detail endpoint, which computes them in one
// round trip (see backend/utilities/evaluationStats.js). They are absent only
// while the detail request is in flight, or on a cached list entity that has
// not been through the detail view yet.
interface EvaluationOverviewStats {
	siteCount?: number
	criteriaCount?: number
	approverCount?: number
	scoreCount?: number
}

const formatDate = (d: string) =>
	new Date(d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})

const formatDateTime = (d: string) =>
	new Date(d).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})

const StatTile = ({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof Building2
	label: string
	value: number
}) => (
	<div className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-100">
		<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
			<Icon className="w-5 h-5" />
		</div>
		<div className="min-w-0">
			<div className="text-2xl font-semibold text-gray-900 leading-tight">{value}</div>
			<div className="text-xs text-gray-500">{label}</div>
		</div>
	</div>
)

const EvaluationOverview = () => {
	const { evaluation } = useOutletContext<EvaluationOutletContext>()
	const stats: EvaluationOverviewStats | undefined = evaluation?.stats
	const siteCount = stats?.siteCount ?? 0
	const criteriaCount = stats?.criteriaCount ?? 0
	const approverCount = stats?.approverCount ?? 0
	const scoreCount = stats?.scoreCount ?? 0

	return (
		<div className="space-y-5">
			{/* Header card */}
			<Card>
				<CardHeader className="pb-3">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							<h2 className="text-xl font-semibold text-gray-900 truncate">{evaluation.name}</h2>
							{evaluation.description && (
								<p className="mt-1.5 text-sm text-gray-600">{evaluation.description}</p>
							)}
						</div>
						<Badge className={cn('shrink-0', STATUS_COLORS[evaluation.status])}>
							{evaluation.status.replace(/_/g, ' ')}
						</Badge>
					</div>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
						{evaluation.marketArea && (
							<span className="flex items-center gap-1.5">
								<MapPin className="w-4 h-4" />
								{evaluation.marketArea}
							</span>
						)}
						{evaluation.portfolio?.assetName && (
							<span className="flex items-center gap-1.5">
								<FileText className="w-4 h-4" />
								Portfolio: {evaluation.portfolio.assetName}
							</span>
						)}
						<span className="flex items-center gap-1.5">
							<Calendar className="w-4 h-4" />
							Created {formatDate(evaluation.createdAt)}
						</span>
					</div>
				</CardContent>
			</Card>

			{/* Stats grid */}
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
				<StatTile icon={Building2} label="Sites" value={siteCount} />
				<StatTile icon={ListChecks} label="Criteria" value={criteriaCount} />
				<StatTile icon={Users} label="Approvers" value={approverCount} />
				<StatTile icon={FileText} label="Scores" value={scoreCount} />
			</div>

			{/* Timeline */}
			<Card>
				<CardHeader className="pb-3">
					<h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Activity</h3>
				</CardHeader>
				<CardContent>
					<div className="space-y-2 text-sm">
						<div className="flex items-center justify-between text-gray-600">
							<span>Created</span>
							<span className="text-gray-400">{formatDateTime(evaluation.createdAt)}</span>
						</div>
						<div className="flex items-center justify-between text-gray-600">
							<span>Last updated</span>
							<span className="text-gray-400">{formatDateTime(evaluation.updatedAt)}</span>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

export default EvaluationOverview
